import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShareAlt,
  faStar as faStarSolid,
  faStar,
  faDownload,
  faChevronDown,
  faShieldAlt,
  faBell,
  faChartBar,
  faCheckCircle,
  faUserCircle,
  faFire,
  faGlobe,
  faMobileAlt,
  faLock,
  faPlay,
  faSpinner
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import ApiServices from "../Services/ApiServices";
import { toastHelper } from "../../Helpers/toastHelper";
import Loader from "../Shared/Loader";
import Header from "../Shared/Header";
import Footer from "../Shared/Footer";
import { getFullFileUrl } from "../../Helpers/UrlHelpers";
import CarouselSlider from "../Shared/CarouselSlider";
import { AppKeys } from "../Shared/AppKeys";

const API_BASE = "AppView/";
const APP_ID = 1;

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const RatingBar = ({ stars, count, total }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="av-rating-row">
      <span className="av-rating-num">{stars}</span>
      <FontAwesomeIcon icon={faStarSolid} className="av-rating-star-mini" />
      <div className="av-rating-progress">
        <motion.div
          className="av-rating-fill"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1.2, ease: "circOut" }}
        />
      </div>
      <span className="av-rating-count">{count.toLocaleString()}</span>
    </div>
  );
};

const StatBox = ({ value, label, icon, index }) => (
  <motion.div
    className="av-stat-card"
    variants={fadeUp}
    custom={index}
    whileHover={{ y: -5 }}
  >
    <div className="av-stat-icon"><FontAwesomeIcon icon={icon} /></div>
    <span className="av-stat-val">{value}</span>
    <span className="av-stat-lbl">{label}</span>
  </motion.div>
);

const FeatureCard = ({ icon, title, desc, index }) => (
  <motion.div
    className="av-feature-card"
    variants={fadeUp}
    custom={index}
    whileHover={{ y: -5 }}
  >
    <div className="av-feature-icon-wrap"><FontAwesomeIcon icon={icon} /></div>
    <h3 className="av-feature-title">{title}</h3>
    <p className="av-feature-desc">{desc}</p>
  </motion.div>
);

const FaqItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      className={`av-faq-item ${open ? "is-open" : ""}`}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: idx * 0.05 }}
    >
      <button className="av-faq-toggle" onClick={() => setOpen(!open)}>
        <span className="av-faq-q">{q}</span>
        <motion.div animate={{ rotate: open ? 180 : 0 }}>
          <FontAwesomeIcon icon={faChevronDown} />
        </motion.div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            className="av-faq-a"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <div className="av-faq-a-inner">{a}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const ApkViewer = () => {
  const [appData, setAppData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rateHover, setRateHover] = useState(null);
  const [rateSubmitting, setRateSubmitting] = useState(false);
  const [userRate, setUserRate] = useState(null);
  const [descExpanded, setDescExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("about");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await ApiServices.get(`${API_BASE}${APP_ID}`);
        if (alive) setAppData(res.data);
        await ApiServices.put(`${API_BASE}updateView/${APP_ID}`, {});
      } catch { setError("تعذر تحميل بيانات التطبيق"); }
      finally { setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const handleDownload = async () => {
    try {
      // TODO: If the API requires a token for the file download, append it to the direct URL as a query parameter (e.g., ?token=YOUR_TOKEN)
      const downloadUrl = `${AppKeys._API_PRODUCTION_URL}App/apk/downaload`;

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toastHelper.success("تم بدء تحميل التطبيق بنجاح!");
      await ApiServices.put(`${API_BASE}updateDownloads/${APP_ID}`, {});
      const res = await ApiServices.get(`${API_BASE}${APP_ID}`);
      setAppData(res.data);
    } catch { toastHelper.error("حدث خطأ أثناء تحميل التطبيق."); }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: "تطبيق لوحة تحكم امتحاني", text: "حمل التطبيق الآن!", url: window.location.href }).catch(() => { });
    } else {
      toastHelper.error("مشاركة التطبيق غير متوفرة في متصفحك");
    }
  };

  const handleRate = async (rate) => {
    setRateSubmitting(true);
    try {
      await ApiServices.put(`${API_BASE}updateRate`, { app_id: APP_ID, rate });
      setUserRate(rate);
      const res = await ApiServices.get(`${API_BASE}${APP_ID}`);
      setAppData(res.data);
    } catch { toastHelper.error("يجب تسجيل الدخول لتقييم التطبيق"); }
    setRateSubmitting(false);
  };

  const screenshots = appData
    ? [appData.sreenshot1, appData.sreenshot2, appData.sreenshot3, appData.sreenshot4, appData.sreenshot5]
      .filter(Boolean).map(getFullFileUrl).filter(Boolean)
    : [];

  const fakeRatings = { 5: 3840, 4: 1120, 3: 390, 2: 130, 1: 75 };
  const totalRatings = Object.values(fakeRatings).reduce((a, b) => a + b, 0);
  const rating = appData?.rate > 0 ? appData.rate.toFixed(1) : "4.8";

  if (loading) return <div className="av-root"><Header /><div className="av-loader-box"><Loader /></div></div>;
  if (error) return <div className="av-root"><Header /><div className="av-error-box">{error}</div></div>;
  if (!appData) return null;

  const tabs = [
    { id: "about", label: "حول التطبيق" },
    { id: "ratings", label: "التقييمات" },
    { id: "details", label: "التفاصيل" },
  ];

  return (
    <div className="av-root" dir="rtl">
      <Header />

      <section className="av-content-section">
        <div className="av-container">

          {/* ── header ── */}
          <motion.div
            className="av-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.div className="av-eyebrow" variants={fadeUp} custom={0}>
              <span className="av-eyebrow-dot" />
              تحميل التطبيق
            </motion.div>
            <motion.h2 className="av-title" variants={fadeUp} custom={1}>
              حمل تطبيق <em>امتحاني الآن</em>
            </motion.h2>
            <motion.p className="av-subtitle" variants={fadeUp} custom={2}>
              لوحة تحكم كاملة للمعلمين والطلاب في جيبك. تجربة تعليمية متكاملة تدعمك أينما كنت.
            </motion.p>
          </motion.div>

          {/* Identity / Hero Content */}
          <motion.div
            className="av-identity-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="av-identity-main">
              <div className="av-icon-wrapper">
                <div className="av-icon-inner">
                  <img src={getFullFileUrl(appData.app_icon)} alt="App Icon" />
                </div>
              </div>

              <div className="av-identity-text">
                <div className="av-badge-list">
                  <span className="av-badge av-badge-purple">تطبيق رسمي</span>
                  <span className="av-badge av-badge-green">إصدار {appData.version}</span>
                </div>
                <h3 className="av-app-name">امتحاني: لوحة المعلم</h3>
                <div className="av-dev-tag">
                  <span>بواسطة فريق امتحاني</span>
                  <FontAwesomeIcon icon={faCheckCircle} className="av-verified-icon" />
                </div>

                <div className="av-main-stats-row">
                  <div className="av-main-stat">
                    <FontAwesomeIcon icon={faStarSolid} className="av-star-main" />
                    <span>{rating}</span>
                  </div>
                  <div className="av-stat-sep" />
                  <div className="av-main-stat">
                    <FontAwesomeIcon icon={faDownload} />
                    <span>{appData.downloads || 0}+ تحميل</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="av-identity-actions">
              <motion.button
                className="av-btn-download"
                onClick={handleDownload}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FontAwesomeIcon icon={faDownload} />
                <span>تحميل (APK)</span>
              </motion.button>

              <motion.button
                className="av-btn-share"
                onClick={handleShare}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FontAwesomeIcon icon={faShareAlt} />
              </motion.button>
            </div>

            <p className="av-os-hint">يدعم Android 6.0 وأحدث · حجم الملف {appData.size} ميجابايت</p>
          </motion.div>

          {/* Quick Info Grid */}
          <motion.div
            className="av-stats-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            <StatBox value={rating} label="التقييم" icon={faStarSolid} index={0} />
            <StatBox value={`${(totalRatings / 1000).toFixed(1)}k`} label="مراجعة" icon={faUserCircle} index={1} />
            <StatBox value={`${appData.downloads || 0}+`} label="تنزيل" icon={faDownload} index={2} />
            <StatBox value={appData.version} label="الإصدار" icon={faMobileAlt} index={3} />
          </motion.div>

          {/* Tabs Navigation */}
          <nav className="av-tabs">
            <div className="av-tabs-inner">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  className={`av-tab-btn ${activeTab === t.id ? "is-active" : ""}`}
                  onClick={() => setActiveTab(t.id)}
                >
                  {t.label}
                  {activeTab === t.id && <motion.div className="av-tab-line" layoutId="avTabLine" />}
                </button>
              ))}
            </div>
          </nav>

          <div className="av-tab-panel">
            <AnimatePresence mode="wait">
              {activeTab === "about" && (
                <motion.div
                  key="about"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.4 }}
                >
                  {/* Screenshots */}
                  {screenshots.length > 0 && (
                    <div className="av-panel-section">
                      <h4 className="av-panel-title">لقطات الشاشة</h4>
                      <div className="av-carousel-container">
                        <CarouselSlider
                          slides={screenshots.map((src, idx) => ({ id: idx, name: "", role: "", image: src }))}
                          title="" useFullScreen={true}
                        />
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  <div className="av-panel-section">
                    <h4 className="av-panel-title">وصف التطبيق</h4>
                    <div className={`av-description-text ${descExpanded ? 'is-expanded' : ''}`}>
                      <p>{appData.description}</p>
                    </div>
                    <button className="av-btn-expand" onClick={() => setDescExpanded(!descExpanded)}>
                      {descExpanded ? "عرض أقل" : "اقرأ المزيد"}
                      <FontAwesomeIcon icon={faChevronDown} className={descExpanded ? 'is-rotated' : ''} />
                    </button>
                  </div>

                  {/* Features */}
                  <div className="av-panel-section">
                    <h4 className="av-panel-title">المميزات الرئيسية</h4>
                    <div className="av-features-grid">
                      <FeatureCard icon={faChartBar} title="تقارير ذكية" desc="متابعة دقيقة لمستوى الطلاب وتقارير أداء مفصلة لحظة بلحظة." index={0} />
                      <FeatureCard icon={faUserCircle} title="إدارة سهلة" desc="تحكم كامل في الطلاب والمجموعات الدراسية من هاتفك." index={1} />
                      <FeatureCard icon={faBell} title="تنبيهات لحظية" desc="ابق على اطلاع دائم بكل جديد من خلال نظام الإشعارات." index={2} />
                    </div>
                  </div>

                  {/* FAQ */}
                  <div className="av-panel-section">
                    <h4 className="av-panel-title">الأسئلة الشائعة</h4>
                    <div className="av-faq-list">
                      <FaqItem q="كيف يمكنني تفعيل حسابي كمعلم؟" a="بعد تحميل التطبيق والتسجيل، سيقوم فريق الدعم الفني بمراجعة بياناتك وتفعيل الحساب خلال 24 ساعة." />
                      <FaqItem q="هل يدعم التطبيق اللغة العربية؟" a="نعم، التطبيق يدعم اللغة العربية والإنجليزية بشكل كامل بناءً على إعدادات هاتفك." />
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === "ratings" && (
                <motion.div
                  key="ratings"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                >
                  <div className="av-ratings-box">
                    <div className="av-ratings-summary">
                      <div className="av-big-score">{rating}</div>
                      <div className="av-stars-display">
                        {[1, 2, 3, 4, 5].map(s => (
                          <FontAwesomeIcon key={s} icon={faStarSolid} className={parseFloat(rating) >= s ? "is-filled" : ""} />
                        ))}
                      </div>
                      <div className="av-review-count">{totalRatings.toLocaleString()} تقييم</div>
                    </div>

                    <div className="av-ratings-bars">
                      <RatingBar stars={5} count={fakeRatings[5]} total={totalRatings} />
                      <RatingBar stars={4} count={fakeRatings[4]} total={totalRatings} />
                      <RatingBar stars={3} count={fakeRatings[3]} total={totalRatings} />
                      <RatingBar stars={2} count={fakeRatings[2]} total={totalRatings} />
                      <RatingBar stars={1} count={fakeRatings[1]} total={totalRatings} />
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === "details" && (
                <motion.div
                  key="details"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                >
                  <div className="av-specs-list">
                    <div className="av-spec-row">
                      <span className="label">اسم الحزمة</span>
                      <span className="value">com.emthany.dashboard</span>
                    </div>
                    <div className="av-spec-row">
                      <span className="label">الإصدار الحالي</span>
                      <span className="value">{appData.version}</span>
                    </div>
                    <div className="av-spec-row">
                      <span className="label">تاريخ التحديث</span>
                      <span className="value">2024/02/10</span>
                    </div>
                    <div className="av-spec-row">
                      <span className="label">المتطلبات</span>
                      <span className="value">Android 6.0+</span>
                    </div>
                    <div className="av-spec-row">
                      <span className="label">الحجم</span>
                      <span className="value">{appData.size} MB</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>
      <Footer />

      <style jsx>{`
        .av-root {
            font-family: "Zain", sans-serif;
          background: #fff;
          min-height: 100vh;
          direction: rtl;
        }

        .av-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .av-content-section {
          padding: 140px 0 100px;
        }

        /* ── Header ── */
        .av-header { text-align: center; margin-bottom: 72px; }
        .av-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          color: #6366f1; font-weight: 800; font-size: 0.85rem;
          letter-spacing: 0.14em; text-transform: uppercase;
          margin-bottom: 24px;
        }
        .av-eyebrow-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
          animation: av-pulse 2.2s ease-in-out infinite;
        }
        @keyframes av-pulse {
          0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.5); }
          50%{ box-shadow: 0 0 0 8px rgba(232,146,10,0); }
        }
        .av-title {
          font-size: clamp(2.4rem, 4.5vw, 4rem);
          font-weight: 900; color: #0f172a; margin: 0 0 20px;
          line-height: 1.18; letter-spacing: -0.025em;
        }
        .av-title em {
          font-style: normal;
          background: linear-gradient(90deg, #6366f1 0%, #6366f1 60%, #6366f1 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .av-subtitle {
          font-size: 1.15rem; color: #64748b; line-height: 1.85; margin: 0 auto;
          max-width: 600px;
        }

        /* ── Identity Card ── */
        .av-identity-card {
          background: #fff;
          border: 1px solid rgba(15,23,42,0.08);
          border-radius: 32px;
          padding: 48px;
          margin-bottom: 48px;
          box-shadow: 0 12px 48px rgba(15,23,42,0.05);
          text-align: center;
        }

        .av-identity-main {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 28px;
          margin-bottom: 36px;
        }

        .av-icon-inner {
          width: 100%; height: 100%; border-radius: 50%; background: #fff;
          padding: 10%; display: flex; align-items: center; justify-content: center;
          box-shadow: 0 8px 30px rgba(0,0,0,0.04);
        }
        .av-icon-inner img { width: 300px; height: 100%; object-fit: contain; }

        .av-badge-list { display: flex; gap: 8px; justify-content: center; margin-bottom: 12px; }
        .av-badge {
          padding: 4px 14px; border-radius: 50px; font-size: 0.8rem; font-weight: 800;
        }
        .av-badge-purple { color: #6366f1; background: rgba(99, 102, 241, 0.08); }
        .av-badge-green { color: #10b981; background: rgba(16, 185, 129, 0.08); }

        .av-app-name { font-size: 2.2rem; font-weight: 900; color: #0f172a; margin-bottom: 8px; }
        .av-dev-tag { display: flex; align-items: center; gap: 8px; color: #64748b; font-weight: 700; font-size: 1rem; justify-content: center; }
        .av-verified-icon { color: #10b981; font-size: 0.9rem; }

        .av-main-stats-row { display: flex; gap: 20px; align-items: center; justify-content: center; margin-top: 16px; }
        .av-main-stat { display: flex; align-items: center; gap: 8px; font-weight: 800; color: #1e293b; font-size: 1.1rem; }
        .av-star-main { color: #6366f1; }
        .av-stat-sep { width: 4px; height: 4px; border-radius: 50%; background: #cbd5e1; }

        .av-identity-actions { display: flex; gap: 16px; justify-content: center; margin-bottom: 24px; }
        .av-btn-download {
          height: 56px; padding: 0 40px; border-radius: 16px; border: none;
          background: #6366f1; color: #fff; font-family: inherit; font-weight: 900;
          font-size: 1.1rem; cursor: pointer; display: flex; align-items: center; gap: 12px;
          box-shadow: 0 10px 25px rgba(99, 102, 241, 0.3); transition: all 0.3s;
        }
        .av-btn-download:hover { transform: translateY(-2px); box-shadow: 0 15px 35px rgba(99, 102, 241, 0.4); }
        .av-btn-download.is-installed { background: #10b981; box-shadow: 0 10px 25px rgba(16, 185, 129, 0.3); }

        .av-btn-share {
          width: 56px; height: 56px; border-radius: 16px; border: 1.5px solid #e2e8f0;
          background: #fff; color: #64748b; cursor: pointer; transition: 0.2s;
          display: flex; align-items: center; justify-content: center; font-size: 1.2rem;
        }
        .av-btn-share:hover { border-color: #6366f1; color: #6366f1; background: #f5f3ff; }

        .av-os-hint { font-size: 0.85rem; color: #94a3b8; font-weight: 600; }

        /* ── Stats Grid ── */
        .av-stats-grid {
          display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px;
          margin-bottom: 60px;
        }
        .av-stat-card {
          background: #fff; padding: 28px; border-radius: 24px; border: 1.5px solid rgba(15,23,42,0.06);
          display: flex; flex-direction: column; align-items: center; gap: 8px;
          transition: 0.3s;
        }
        .av-stat-card:hover { border-color: rgba(15,23,42,0.12); box-shadow: 0 8px 30px rgba(0,0,0,0.03); }
        .av-stat-icon { font-size: 1.3rem; color: #6366f1; margin-bottom: 4px; }
        .av-stat-val { font-size: 1.6rem; font-weight: 900; color: #0f172a; line-height: 1; }
        .av-stat-lbl { font-size: 0.9rem; color: #94a3b8; font-weight: 700; }

        /* ── Tabs ── */
        .av-tabs { border-bottom: 2px solid #f1f5f9; margin-bottom: 48px; }
        .av-tabs-inner { display: flex; gap: 40px; }
        .av-tab-btn {
          border: none; background: none; padding: 16px 0; font-family: inherit;
          font-weight: 800; font-size: 1.15rem; color: #94a3b8; cursor: pointer;
          position: relative; transition: color 0.3s;
        }
        .av-tab-btn.is-active { color: #0f172a; }
        .av-tab-line {
          position: absolute; bottom: -2px; left: 0; right: 0;
          height: 3px; background: #6366f1; border-radius: 10px;
        }

        /* ── Panel Sections ── */
        .av-panel-section { margin-bottom: 64px; }
        .av-panel-title { font-size: 1.5rem; font-weight: 900; color: #0f172a; margin-bottom: 24px; }

        .av-carousel-container { background: #f8fafc; border-radius: 24px; padding: 24px; border: 1px solid #f1f5f9; }

        .av-description-text { line-height: 1.9; color: #475569; font-weight: 500; font-size: 1.15rem; overflow: hidden; max-height: 140px; transition: 0.5s; }
        .av-description-text.is-expanded { max-height: 2000px; }
        .av-btn-expand {
          background: #f8fafc; border: none; color: #6366f1; font-weight: 800;
          cursor: pointer; margin-top: 20px; display: flex; align-items: center; gap: 10px;
          padding: 8px 20px; border-radius: 50px; font-size: 0.95rem;
        }
        .av-btn-expand .is-rotated { transform: rotate(180deg); }

        .av-features-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 24px; }
        .av-feature-card {
          padding: 40px; background: #fff; border-radius: 28px; border: 1.5px solid rgba(15,23,42,0.06);
          transition: 0.3s;
        }
        .av-feature-card:hover { border-color: rgba(15,23,42,0.12); transform: translateY(-5px); box-shadow: 0 12px 32px rgba(15,23,42,0.05); }
        .av-feature-icon-wrap {
          width: 56px; height: 56px; background: rgba(99, 102, 241, 0.08); color: #6366f1;
          border-radius: 14px; display: flex; align-items: center; justify-content: center;
          font-size: 1.4rem; margin-bottom: 24px;
        }
        .av-feature-title { font-size: 1.35rem; font-weight: 800; margin-bottom: 12px; color: #0f172a; }
        .av-feature-desc { font-size: 1rem; color: #64748b; line-height: 1.7; font-weight: 500; }

        .av-faq-list { display: flex; flex-direction: column; gap: 16px; }
        .av-faq-item { border: 1.5px solid #f1f5f9; border-radius: 20px; transition: 0.3s; }
        .av-faq-item.is-open { border-color: #e2e8f0; }
        .av-faq-toggle {
          width: 100%; padding: 24px; border: none; background: transparent;
          display: flex; justify-content: space-between; align-items: center;
          cursor: pointer; font-family: inherit; font-weight: 800; font-size: 1.1rem;
          color: #0f172a; text-align: right;
        }
        .av-faq-a-inner { padding: 0 24px 24px; color: #64748b; line-height: 1.8; font-weight: 600; font-size: 1.05rem; border-top: 1px dashed #f1f5f9; padding-top: 20px; }

        /* ── Ratings ── */
        .av-ratings-box { display: flex; gap: 80px; padding: 60px; background: #fff; border: 1.5px solid #f1f5f9; border-radius: 32px; align-items: center; }
        .av-ratings-summary { text-align: center; }
        .av-big-score { font-size: 6rem; font-weight: 900; line-height: 1; color: #0f172a; margin-bottom: 15px; }
        .av-stars-display { color: #e2e8f0; font-size: 1.6rem; display: flex; gap: 6px; justify-content: center; }
        .av-stars-display .is-filled { color: #6366f1; }
        .av-review-count { margin-top: 15px; color: #94a3b8; font-weight: 700; font-size: 1.1rem; }
        .av-ratings-bars { flex: 1; display: flex; flex-direction: column; gap: 12px; }
        .av-rating-row { display: flex; align-items: center; gap: 16px; }
        .av-rating-num { font-weight: 900; width: 15px; font-size: 1.1rem; }
        .av-rating-star-mini { color: #cbd5e1; font-size: 0.9rem; }
        .av-rating-progress { flex: 1; height: 10px; background: #f1f5f9; border-radius: 10px; overflow: hidden; }
        .av-rating-fill { height: 100%; background: #6366f1; border-radius: 10px; }
        .av-rating-count { min-width: 60px; color: #94a3b8; font-weight: 700; text-align: left; }

        .av-specs-list { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px; }
        .av-spec-row {
          display: flex; justify-content: space-between; padding: 24px;
          background: #fff; border: 1.5px solid #f1f5f9; border-radius: 20px;
        }
        .av-spec-row .label { color: #64748b; font-weight: 700; }
        .av-spec-row .value { color: #0f172a; font-weight: 800; }

        .av-loader-box { height: 70vh; display: flex; align-items: center; justify-content: center; }
        .av-error-box { height: 70vh; display: flex; align-items: center; justify-content: center; color: #ef4444; font-weight: 800; }

        @media (max-width: 1024px) {
          .av-stats-grid { grid-template-columns: repeat(2, 1fr); }
          .av-ratings-box { flex-direction: column; gap: 40px; padding: 40px; }
        }

        @media (max-width: 768px) {
          .av-identity-card { padding: 32px 20px; }
          .av-app-name { font-size: 1.8rem; }
          .av-identity-actions { flex-direction: column; align-items: stretch; }
          .av-btn-download { width: 100%; justify-content: center; }
          .av-btn-share { width: 100%; height: 50px; }
          .av-tabs-inner { gap: 20px; }
          .av-tab-btn { font-size: 1rem; }
        }

        @media (max-width: 480px) {
          .av-stats-grid { grid-template-columns: 1fr; }
          .av-big-score { font-size: 4rem; }
          .av-header { margin-bottom: 48px; }
        }
      `}</style>
    </div>
  );
};

export default ApkViewer;